<template>
  <div
    ref="dnsFormWrapper"
    class="dns-form w-full md:w-343px lg:w-704px rounded-16px pb-32px md:mx-auto"
  >
    <div
      class="text-center pt-24px rounded-t-16px"
      :style="{ backgroundColor: getBgColor }"
    >
      <h2 class="text-32 mt-6 mb-6 font-bold leading-dot92">
        Do Not Sell or Share My Personal Information
      </h2>

      <p class="text-18 mt-4 leading-1-56 p-8">
        {{ formattedMessage }}
      </p>
    </div>
    <form action="" method="POST" ref="dnsForm">
      <div class="mt-6 px-6">
        <div class="vx-row">
          <input type="hidden" name="_token" :value="csrf" />

          <!-- First Name Field-->
          <div class="vx-col w-full md:w-1/2 mb-base relative mt-4">
            <label class="block mb-1 text-xs text-onyx-light">First Name</label>
            <vs-input
              v-validate="{ required: true, alpha_spaces: true, is_not: last_name }"
              name="first_name"
              v-model.trim="first_name"
              class="w-full h-42px"
              maxlength="40"
              autofocus
            ></vs-input>
            <span
              class="text-danger text-10"
              v-show="errors.has('first_name')"
              >{{ errors.first('first_name') }}</span
            >
          </div>

          <!-- Last Name Field-->
          <div class="vx-col w-full md:w-1/2 mb-base relative mt-4">
            <label class="block mb-1 text-xs text-onyx-light">Last Name</label>
            <vs-input
              v-validate="{ required: true, alpha_spaces: true, is_not: first_name }"
              name="last_name"
              v-model.trim="last_name"
              class="w-full h-42px"
              maxlength="40"
            ></vs-input>
            <span
              class="text-danger text-10"
              v-show="errors.has('last_name')"
              >{{ errors.first('last_name') }}</span
            >
          </div>

          <!-- Email Field-->
          <div class="vx-col relative w-full md:w-1/2 mb-base mt-4">
            <label class="block mb-1 text-xs text-onyx-light">Email</label>
            <vs-input
              v-validate="'required|email'"
              name="email"
              v-model="email"
              class="w-full h-42px"
              maxlength="100"
            ></vs-input>
            <span class="text-danger text-10" v-show="errors.has('email')">{{
              errors.first('email')
            }}</span>
          </div>

          <!-- Phone Number Field-->
          <div class="vx-col relative w-full md:w-1/2 mb-base mt-4">
            <label class="block mb-1 text-xs text-onyx-light"
              >Mobile Phone</label
            >
            <vs-input
              type="tel"
              v-validate="{
                required: true,
                regex: /^(\(?[0-9]{1,3}\)?)(\s?[0-9]{0,3}-?[0-9]{0,4})$/,
              }"
              @input="setDirty('phone')"
              @blur="validatePhoneNumberOnBlur"
              name="phone"
              v-model="phone"
              class="w-full h-42px"
              maxlength="14"
            ></vs-input>
            <span class="text-danger text-10" v-show="errors.has('phone')">{{
              errors.first('phone')
            }}</span>
          </div>

          <div class="vx-col relative w-full md:w-1/2 mb-base mt-4">
            <label class="block mb-1 text-xs text-onyx-light">State</label>
            <v-select
              v-validate="{ required: true }"
              placeholder="Select State"
              :options="stateOptions"
              name="state"
              v-model="state"
              class="w-full state-options"
            />
            <!--Hack to support v-select as regular html form submission-->
            <input type="hidden" name="state" :value="state" />
            <span class="text-danger text-10" v-show="errors.has('state')">{{
              errors.first('state')
            }}</span>
          </div>

          <div class="vx-col relative w-full md:w-1/2 mb-base mt-4"></div>

          <div class="vx-col relative w-full mt-8">
            <label
              class="check-container md:w-420px block mb-1 text-xs text-onyx-light pr-7"
              >Opt out of Targeted Marketing for {{ brand_name }} Only:
              <input
                type="checkbox"
                v-validate="'required'"
                name="opt_out"
                value="product only"
                @change="singleSelect"
                v-model="opt_out"
              />
              <span class="checkmark"></span>
            </label>
          </div>

          <div class="vx-col relative w-full mt-4">
            <label
              class="check-container md:w-420px block mb-1 text-xs text-onyx-light pr-24"
              >Opt out of All CNB Targeted Marketing:
              <input
                type="checkbox"
                v-validate="'required'"
                name="opt_out"
                value="all products"
                @change="singleSelect"
                v-model="opt_out"
              />
              <span class="checkmark"></span>
            </label>
            <span class="text-danger text-10" v-show="errors.has('opt_out')">{{
              errors.first('opt_out')
            }}</span>
          </div>
        </div>

        <p class="text-10 mt-10 privacy text-center">
          For more information on our privacy practices, please refer to our
          <a target="_blank" :href="`privacy-policy`" class=""
            >Privacy Policy.</a
          >
        </p>
        <!-- Submit Button -->
        <primary-button
          size="medium"
          class="w-50 mt-6 block mx-auto primary"
          @click="submitForm"
          :disabled="loading"
          :style="getCustomStyle"
        >
          Submit
          <span class="ml-2" v-if="loading"
            ><i class="fa-solid fa-circle-notch fa-spin"></i
          ></span>
        </primary-button>
      </div>
    </form>
  </div>
</template>
<script>
import { Validator } from 'vee-validate';
import FormatPhoneNumberMixin from '../../mixins/FormatPhoneNumberMixin';
import HandleBackEndErrorsMixin from '../../mixins/HandleBackEndErrorsMixin';
import MediaRadioButton from '../../components/MediaRadioButton.vue';
import event from '../../event';

export default {
  name: 'DnsForm',
  components: { MediaRadioButton },
  mixins: [FormatPhoneNumberMixin, HandleBackEndErrorsMixin],
  props: {
    brand_name: {
      type: String,
    },
    stateOptions: {
      type: Array,
      required: true,
    }
  },

  mounted() {
    // Apply watchers after all other initialization is done;
    this.applyFullFormWatchers();
    this.loading = false;

    this.formHeight();
  },

  data() {
    return {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      opt_out: [],
      state: '',
      loading: false,
    };
  },

  watch: {
    formData: {
      handler() {},
      deep: true,
    },
  },

  created() {
    let dict = {
      custom: {
        first_name: {
          required: 'First Name field is required',
          is_not: 'First Name must be different from Last Name',
          alpha_spaces:
            'Your First Name may only contain alphabetic characters',
        },
        last_name: {
          required: 'Last Name field is required',
          is_not: 'Last Name must be different from First Name',
          alpha_spaces: 'Your Last Name may only contain alphabetic characters',
        },
        email: {
          required: 'Email field is required',
          email: 'Enter a valid email address',
        },
        phone: {
          required: 'Mobile Phone field is required',
          regex: 'The mobile phone field format is invalid',
        },
        state: {
          required: 'State field is required',
          alpha_spaces: 'Please select a state',
        },
        opt_out: {
          required: 'Opt Out field is required',
          alpha_spaces: 'Please select an Opt Out',
        },
      },
    };

    // register custom messages
    Validator.localize('en', dict);
  },

  computed: {
    csrf() {
      return document
        .querySelector('meta[name="csrf-token"]')
        .getAttribute('content');
    },
    formattedMessage() {
      if (!this.stateOptions || this.stateOptions.length === 0) {
        return 'This form cannot be submitted by any residents.';
      } else if (this.stateOptions.length === 1) {
        return `This form may only be submitted by or on behalf of ${this.stateOptions[0]} residents.`;
      } else {
        const lastOption = this.stateOptions.slice(-1)[0];
        const formattedOptions = this.stateOptions.slice(0, -1).join(', ');
        return `This form may only be submitted by or on behalf of ${formattedOptions} or ${lastOption} residents.`;
      }
    },
    cssVars() {
      return {
        '--company-color': '#1f4484',
      };
    },
    getCustomStyle() {
      if (this.overrideBgColor) {
        return `background-color: ${this.overrideBgColor}`;
      }

      return '';
    },
    getBgColor() {
      if (this.overrideBgColor) {
        return this.overrideBgColor;
      }
    },

    formData() {
      const data = { ...this.$data, ...this.$data.dnsFullForm };

      delete data.stateOptions;
      delete data.loading;

      return data;
    },
    // color() {
    //   return this.color;
    //   return this.company ? this.company['color'] : '#002f65';
    // },
  },

  methods: {
    singleSelect(e) {
      this.opt_out = [];
      this.opt_out.push(e.target.value);
    },
    toggleWithTrashed() {
      return true;
    },
    async formHeight() {
      await this.$nextTick();

      let height = this.$refs.dnsFormWrapper.clientHeight;
      event.bus.$emit('dns_form_height', height);
    },
    applyFullFormWatchers() {
      // dnsFullForm.valid_cdl
      this.$watch(
        'dnsFullForm.valid_cdl',
        newValue => {
          if (newValue === 'no') {
            this.dnsFullForm.experience = 'no experience';
          }
        },
        { deep: true },
      );
    },

    async submitForm(e) {
      const result = await this.$validator.validateAll();
      if (result && this.isPhoneNumberValid(this.phone)) {
        this.loading = true;

        this.$refs.dnsForm.submit();
      }
    },
    formatPhoneNumber(ev) {
      this.phone = this.setFormatPhoneNumber(ev, this.phone);
    },
    isPhoneNumberValid(input) {
      var validatePhoneNumberRegex = /^\([0-9]{3}\)\s[0-9]{3}-[0-9]{4}$/;
      if (!validatePhoneNumberRegex.test(input)) {
        this.errors.add({
          field: 'phone',
          msg: 'The mobile phone field format is invalid',
        });
        return false;
      }
      return true;
    },
    validatePhoneNumberOnBlur(e) {
      this.formatPhoneNumber(e);
      this.isPhoneNumberValid(e.target.value);
    },
  },
};
</script>

<style lang="scss">
.dns-form {
  .privacy {
    a {
      color: blue !important;
      text-decoration: underline !important;
    }
  }

  select {
    font-family: 'Montserrat', sans-serif !important;
    font-size: 14px !important;
    color: #202028 !important;

    &::placeholder,
    &::-moz-placeholder,
    &::-webkit-input-placeholder,
    &:-ms-input-placeholder {
      font-family: 'Montserrat', sans-serif !important;
      font-size: 14px !important;
      color: #202028 !important;
    }
  }

  .vs-con-input input {
    font-family: 'Montserrat', sans-serif !important;
    color: #202028 !important;
  }
  .v-select {
    ul {
      li {
        &:hover {
          background-color: #202028;
        }
      }
    }
    .vs__selected {
      background: var(--company-color) !important;
    }
  }

  .vs__dropdown-menu {
    .vs__dropdown-option.vs__dropdown-option--highlight {
      background: #1f4484 !important;
      color: white !important;
    }
  }
  $background-color_1: #eee;
  $background-color_2: #ccc;
  $background-color_3: #2196f3;

  .check-container {
    display: block;
    position: relative;
    margin-bottom: 12px;
    cursor: pointer;
    font-size: 22px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;
      &:checked {
        ~ {
          .checkmark {
            background-color: var(--primary);
            &:after {
              display: block;
            }
          }
        }
      }
    }
    &:hover {
      input {
        ~ {
          .checkmark {
            background-color: var(--primary);
          }
        }
      }
    }
    .checkmark {
      &:after {
        left: 7px;
        top: 2px;
        width: 5px;
        height: 10px;
        border: solid white;
        border-width: 0 3px 3px 0;
        -webkit-transform: rotate(45deg);
        -ms-transform: rotate(45deg);
        transform: rotate(45deg);
      }
    }
  }
  .checkmark {
    position: absolute;
    top: 0;
    right: 0;
    height: 20px;
    width: 20px;
    background-color: $background-color_1;
    &:after {
      content: '';
      position: absolute;
      display: none;
    }
  }
}
</style>
<style>
.v-list-item__title:hover {
  color: #ffd54f !important;
}

.v-list .v-list-item--active {
  background-color: green !important;
}
.v-list .v-list-item--active .v-list-item__title {
  color: #ffd54f !important;
}
</style>
